import React from 'react';
import { SystemMessageDto } from '../../services/applications/types';
import ModalPane from '../UI/ModalPane';
import Tabs, { TabItem } from '../UI/Tabs';
import Button from '../UI/Button';

interface PromptDetailsViewProps {
  prompt: SystemMessageDto;
  isOpen: boolean;
  onClose: () => void;
}

/**
 * Componente para visualizar detalhes de um prompt em um modal com abas
 */
const PromptDetailsView: React.FC<PromptDetailsViewProps> = ({
  prompt,
  isOpen,
  onClose
}) => {
  // Formatar booleanos para visualização
  const formatBoolean = (value: boolean | string | undefined) => {
    if (value === undefined) return '-';
    if (typeof value === 'string') {
      return value.toLowerCase() === 'true' ? 'Sim' : 'Não';
    }
    return value ? 'Sim' : 'Não';
  };

  // Formatar data para visualização
  const formatDate = (dateString: string | undefined) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleString('pt-BR');
  };

  // Função para destacar parâmetros entre chaves no texto
  const highlightParameters = (text: string | undefined): React.ReactNode => {
    if (!text) return 'Nenhum conteúdo definido';

    // Dividir o texto pelos parâmetros entre chaves {param}
    const parts = text.split(/(\{[^}]+\})/g);

    return parts.map((part, index) => {
      // Verificar se o texto corresponde a um parâmetro {param}
      if (part.match(/^\{[^}]+\}$/)) {
        return (
          <span key={index} className="bg-yellow-200 text-yellow-800 px-1 rounded font-semibold">
            {part}
          </span>
        );
      }
      return part;
    });
  };
  
  // Log detalhado dos dados do prompt para diagnóstico
  console.log("PromptDetailsView - Dados recebidos:", prompt);
  console.log("PromptDetailsView - message:", prompt.message);
  console.log("PromptDetailsView - system_message:", prompt.system_message);
  console.log("PromptDetailsView - user_message:", prompt.user_message);
  console.log("PromptDetailsView - tools:", prompt.tools, "Tipo:", Array.isArray(prompt.tools) ? "Array" : typeof prompt.tools);
  console.log("PromptDetailsView - is_active:", prompt.is_active, "Tipo:", typeof prompt.is_active);
  console.log("PromptDetailsView - with_rag:", prompt.with_rag, "Tipo:", typeof prompt.with_rag);
  console.log("PromptDetailsView - temperature:", prompt.temperature, "Tipo:", typeof prompt.temperature);

  // State for active tab
  const [activeTab, setActiveTab] = React.useState<string>('settings');
  
  // Criar abas para o modal
  const tabs: TabItem[] = [
    {
      id: 'settings',
      label: 'Configurações',
      icon: <i className="fas fa-cog mr-2"></i>,
      content: (
        <div className="prose max-w-none">
          <h3 className="text-lg font-medium text-gray-900 mt-0">Parâmetros de Geração</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
              <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
                <h4 className="text-sm font-medium text-gray-700 mb-0">Configurações do Modelo</h4>
              </div>
              <div className="p-4 space-y-4">
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium text-gray-600">Temperatura:</span>
                  <span className="text-sm bg-blue-50 text-blue-700 py-1 px-2 rounded font-mono">
                    {typeof prompt.temperature === 'number' 
                      ? prompt.temperature.toFixed(2) 
                      : prompt.temperature || '-'}
                  </span>
                </div>
                
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium text-gray-600">Provedor:</span>
                  <span className="text-sm bg-purple-50 text-purple-700 py-1 px-2 rounded">
                    {prompt.provider_name || '-'}
                  </span>
                </div>
                
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium text-gray-600">Modelo:</span>
                  <span className="text-sm bg-purple-50 text-purple-700 py-1 px-2 rounded">
                    {prompt.model_name || '-'}
                  </span>
                </div>
              </div>
            </div>
            
            <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
              <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
                <h4 className="text-sm font-medium text-gray-700 mb-0">Estado e Status</h4>
              </div>
              <div className="p-4 space-y-4">
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium text-gray-600">Status do Usuário:</span>
                  <span className="text-sm bg-yellow-50 text-yellow-700 py-1 px-2 rounded font-medium">
                    {prompt.user_status || 'Todos'}
                  </span>
                </div>
                
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium text-gray-600">Uso de RAG:</span>
                  <span className={`text-sm py-1 px-2 rounded font-medium ${
                    prompt.with_rag 
                      ? 'bg-green-50 text-green-700' 
                      : 'bg-gray-50 text-gray-500'
                  }`}>
                    {formatBoolean(prompt.with_rag)}
                  </span>
                </div>
                
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium text-gray-600">Ativo:</span>
                  <span className={`text-sm py-1 px-2 rounded font-medium ${
                    prompt.is_active 
                      ? 'bg-green-50 text-green-700' 
                      : 'bg-red-50 text-red-700'
                  }`}>
                    {formatBoolean(prompt.is_active)}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Ferramentas disponíveis */}
          <h3 className="text-lg font-medium text-gray-900 mt-6">Ferramentas Disponíveis</h3>
          <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
            <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
              <h4 className="text-sm font-medium text-gray-700 mb-0">Integração com Ferramentas</h4>
            </div>
            <div className="p-4">
              {Array.isArray(prompt.tools) && prompt.tools.length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {prompt.tools.map((tool, index) => (
                    <div key={index} className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm flex items-center">
                      <i className="fas fa-tools text-xs mr-2"></i>
                      <span className="font-medium">{tool}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 italic text-sm">Nenhuma ferramenta disponível para este prompt.</p>
              )}
            </div>
          </div>
        </div>
      )
    },
    {
      id: 'main-content',
      label: 'Conteúdo Principal',
      icon: <i className="fas fa-file-alt mr-2"></i>,
      content: (
        <div className="prose max-w-none">
          <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden mb-6">
            <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
              <h4 className="text-sm font-medium text-gray-700 mb-0 flex items-center">
                <i className="fas fa-robot text-blue-500 mr-2"></i>
                Mensagem do Sistema
              </h4>
            </div>
            <div className="p-0">
              <div className="bg-gray-50 p-5 whitespace-pre-wrap font-mono text-sm overflow-auto max-h-[450px] border-l-4 border-blue-300">
                {highlightParameters(prompt.message || prompt.system_message || 'Nenhum conteúdo definido')}
              </div>
            </div>
          </div>

          {/* Mostrar user_message se existir */}
          {prompt.user_message && (
            <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
              <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
                <h4 className="text-sm font-medium text-gray-700 mb-0 flex items-center">
                  <i className="fas fa-user text-green-500 mr-2"></i>
                  Mensagem do Usuário
                </h4>
              </div>
              <div className="p-0">
                <div className="bg-gray-50 p-5 whitespace-pre-wrap font-mono text-sm overflow-auto max-h-[300px] border-l-4 border-green-300">
                  {highlightParameters(prompt.user_message)}
                </div>
              </div>
            </div>
          )}
        </div>
      )
    },
    {
      id: 'media-content',
      label: 'Conteúdo para Mídias',
      icon: <i className="fas fa-image mr-2"></i>,
      content: (
        <div className="prose max-w-none">
          <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
            <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
              <h4 className="text-sm font-medium text-gray-700 mb-0 flex items-center">
                <i className="fas fa-image text-purple-500 mr-2"></i>
                Instruções para Processamento de Mídia
              </h4>
            </div>
            <div className="p-0">
              {prompt.system_media ? (
                <div className="bg-gray-50 p-5 whitespace-pre-wrap font-mono text-sm overflow-auto max-h-[450px] border-l-4 border-purple-300">
                  {highlightParameters(prompt.system_media)}
                </div>
              ) : (
                <div className="p-6 text-center">
                  <i className="fas fa-photo-video text-gray-300 text-5xl mb-3"></i>
                  <p className="text-gray-500 italic text-sm">Nenhuma instrução específica para mídia definida.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )
    },
    {
      id: 'deferred-content',
      label: 'Mensagens Represadas',
      icon: <i className="fas fa-clock mr-2"></i>,
      content: (
        <div className="prose max-w-none">
          <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
            <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
              <h4 className="text-sm font-medium text-gray-700 mb-0 flex items-center">
                <i className="fas fa-clock text-orange-500 mr-2"></i>
                Instruções para Mensagens Represadas
              </h4>
            </div>
            <div className="p-0">
              {prompt.system_deferred_message ? (
                <div className="bg-gray-50 p-5 whitespace-pre-wrap font-mono text-sm overflow-auto max-h-[450px] border-l-4 border-orange-300">
                  {highlightParameters(prompt.system_deferred_message)}
                </div>
              ) : (
                <div className="p-6 text-center">
                  <i className="fas fa-clock text-gray-300 text-5xl mb-3"></i>
                  <p className="text-gray-500 italic text-sm">Nenhuma instrução específica para mensagens represadas definida.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )
    },
    {
      id: 'metadata',
      label: 'Metadados',
      icon: <i className="fas fa-info-circle mr-2"></i>,
      content: (
        <div className="prose max-w-none">
          <h3 className="text-lg font-medium text-gray-900 mt-0">Informações do Sistema</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
              <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
                <h4 className="text-sm font-medium text-gray-700 mb-0">Identificação</h4>
              </div>
              <div className="p-4 space-y-4">
                <div className="flex flex-col">
                  <span className="text-xs font-medium text-gray-500 mb-1">ID do Prompt</span>
                  <code className="text-sm bg-gray-50 p-2 rounded font-mono text-purple-700 break-all">{prompt.id || '-'}</code>
                </div>
                
                <div className="flex flex-col">
                  <span className="text-xs font-medium text-gray-500 mb-1">ID da Aplicação</span>
                  <code className="text-sm bg-gray-50 p-2 rounded font-mono text-purple-700 break-all">{prompt.application_id || '-'}</code>
                </div>
              </div>
            </div>
            
            <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
              <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
                <h4 className="text-sm font-medium text-gray-700 mb-0">Timestamps</h4>
              </div>
              <div className="p-4 space-y-4">
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium text-gray-600">Criado em:</span>
                  <span className="text-sm bg-gray-50 text-gray-800 py-1 px-2 rounded">
                    {formatDate(prompt.created_at)}
                  </span>
                </div>
                
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium text-gray-600">Atualizado em:</span>
                  <span className="text-sm bg-gray-50 text-gray-800 py-1 px-2 rounded">
                    {formatDate(prompt.updated_at)}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )
    }
  ];

  return (
    <ModalPane 
      isOpen={isOpen} 
      onClose={onClose}
      title={
        <div className="flex items-center">
          <i className="fas fa-file-alt text-blue-500 mr-2"></i>
          <span>Detalhes do Prompt</span>
          {prompt.user_status && (
            <span className="ml-3 text-xs bg-blue-100 text-blue-800 py-1 px-2 rounded-full">
              {prompt.user_status}
            </span>
          )}
        </div>
      }
      size="xl"
      footer={
        <Button 
          variant="secondary" 
          onClick={onClose}
          leftIcon={<i className="fas fa-times-circle mr-1"></i>}
        >
          Fechar
        </Button>
      }
    >
      <div className="py-3">
        {/* Tabs Navigation */}
        <Tabs tabs={tabs} activeTab={activeTab} onChange={setActiveTab} />
        
        {/* Tab Content */}
        <div className="pt-4 mt-2">
          {tabs.find(tab => tab.id === activeTab)?.content}
        </div>
      </div>
    </ModalPane>
  );
};

export default PromptDetailsView;